import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { createTest, selectTest, setExamId } from '../../../store/testSelectionSlice';
import { getUserKcCardData, saveUserKcPathData } from '../../../services/MyPathService';
import { resetExam, setSection, updateQuestionData } from '../../../store/ExamSlice';

import PathCard from './PathCard';
import PathChooseCard from './pathChooseCard';
import SftAiPathIcon from '../../../assets/SftAiPathIcon.svg';
import axiosRequestInterceptor from '../../../interceptor/axiosAPIRequestInterceptor';
import { fetchLearningMapDetails } from '../../../services/DashboardApi';
import { logger } from '../../../utils/logger';
import { set } from 'lodash';
import { use } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const { v4: uuid } = require('uuid');

const MyPathDashboard = () => {
	const [cardsRW, setCardsRW] = useState([
		{ id: uuid(), topicId: '', subtopicId: '', saved: false },
	]);
	const [cardsM, setCardsM] = useState([
		{ id: uuid(), topicId: '', subtopicId: '', saved: false },
	]);
	const [topicMapRW, setTopicMapRW] = useState({});
	const [topicMapM, setTopicMapM] = useState({});

	const { topiclist } = useSelector((state) => state.selectedSubTopic);
	const { userId } = useSelector((state) => state.userDetails);

	const [readingTopics, setReadingTopics] = useState([]);
	const [readingSubtopicsByTopic, setReadingSubtopicsByTopic] = useState({});

	const [mathTopics, setMathTopics] = useState([]);
	const [mathSubtopicsByTopic, setMathSubtopicsByTopic] = useState({});
	const [alert, setAlert] = useState(false);
	const [alertMsg, setAlertMsg] = useState('');

	const alertTimeoutRef = useRef(null);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const api = axiosRequestInterceptor;

	const removeUsedSubtopicsFromAvailableList = (cards, topics, subtopicsByTopic) => {
		const updatedSubtopicsByTopic = { ...subtopicsByTopic };
		const updatedTopics = [...topics];

		// Build a map of topicId → Set of subtopicIds to remove
		const usedMap = {};
		for (const card of cards) {
			if (!usedMap[card.topicId]) usedMap[card.topicId] = new Set();
			usedMap[card.topicId].add(card.subtopicId);
		}

		// Remove subtopics
		for (const topicId in usedMap) {
			const originalList = updatedSubtopicsByTopic[topicId];
			if (!originalList) continue;

			const filtered = originalList.filter((sub) => !usedMap[topicId].has(sub.id));

			if (filtered.length === 0) {
				// No subtopics left → remove the topic
				delete updatedSubtopicsByTopic[topicId];
				const indexToRemove = updatedTopics.findIndex((t) => t.id === topicId);
				if (indexToRemove >= 0) {
					updatedTopics.splice(indexToRemove, 1);
				}
			} else {
				// Update with filtered subtopics
				updatedSubtopicsByTopic[topicId] = filtered;
			}
		}

		return {
			topics: updatedTopics,
			subtopicsByTopic: updatedSubtopicsByTopic,
		};
	};

	useEffect(() => {
		const fetchData = async (subject) => {
			try {
				const response = await fetchLearningMapDetails(userId, subject);
				logger.log('Response from API: ', response.data);
				return response.data;
			} catch (error) {
				logger.log('Error fetching topics: ', error);
				// alert('There was an error please try again later');
			}
		};

		const fetchAndSetTopics = async () => {
			let rawMathTopics = await fetchData('MATHS');
			let rawReadingTopics = await fetchData('READING_WRITING');

			rawMathTopics = rawMathTopics?.reduce((acc, topicObj) => {
				acc[topicObj.TOPIC_ID] = {
					subtopics: topicObj.SUBTOPICS,
					topicName: topicObj.TOPIC_NAME,
				};
				return acc;
			}, {});
			rawReadingTopics = rawReadingTopics?.reduce((acc, topicObj) => {
				acc[topicObj.TOPIC_ID] = {
					subtopics: topicObj.SUBTOPICS,
					topicName: topicObj.TOPIC_NAME,
				};
				return acc;
			}, {});

			const formattedMathTopic = Object.entries(rawMathTopics)?.map(([id, value]) => ({
				id,
				name: value.topicName,
			}));

			const formattedMathSubTopics = Object.entries(rawMathTopics)?.reduce(
				(acc, [topicId, value]) => {
					acc[topicId] = value.subtopics.map((sub) => ({
						id: sub.SUBTOPIC_ID,
						name: sub.SUBTOPIC_NAME,
						progress: sub.SUBTOPIC_PROGRESS,
					}));
					return acc;
				},
				{}
			);

			const formattedReadingTopics = Object.entries(rawReadingTopics)?.map(([id, value]) => ({
				id,
				name: value.topicName,
			}));

			const formattedReadingSubtopics = Object.entries(rawReadingTopics)?.reduce(
				(acc, [topicId, value]) => {
					acc[topicId] = value.subtopics.map((sub) => ({
						id: sub.SUBTOPIC_ID,
						name: sub.SUBTOPIC_NAME,
						progress: sub.SUBTOPIC_PROGRESS,
					}));
					return acc;
				},
				{}
			);

			setMathTopics(formattedMathTopic);

			setMathSubtopicsByTopic(formattedMathSubTopics);

			setReadingTopics(formattedReadingTopics);

			setReadingSubtopicsByTopic(formattedReadingSubtopics);
			return {
				formattedMathTopic,
				formattedMathSubTopics,
				formattedReadingTopics,
				formattedReadingSubtopics,
			};
		};

		const fetchAndSetData = async (
			formattedMathTopic,
			formattedMathSubTopics,
			formattedReadingTopics,
			formattedReadingSubtopics
		) => {
			const response = await getUserKcCardData(userId);
			if (response.error) {
				logger.log('Error fetching user KC card data: ', response.error);
				setAlert(true);
				setAlertMsg('There was no cards saved');
				if (alertTimeoutRef.current) {
					clearTimeout(alertTimeoutRef.current);
				}
				alertTimeoutRef.current = setTimeout(() => {
					setAlert(false);
					alertTimeoutRef.current = null;
				}, 3000);
			}
			const { READING_WRITING, MATHS } = response.data;
			if (READING_WRITING?.length > 0) {
				setCardsRW(READING_WRITING);
				READING_WRITING.forEach((card) => {
					if (card?.topicId && card?.subtopicId) {
						addSubtopicToTopicMap(card.topicId, card.subtopicId, 'RW');
					}
				});
				const { topics, subtopicsByTopic } = removeUsedSubtopicsFromAvailableList(
					READING_WRITING,
					formattedReadingTopics,
					formattedReadingSubtopics
				);
				setReadingTopics(topics);
				setReadingSubtopicsByTopic(subtopicsByTopic);
			}
			if (MATHS?.length > 0) {
				setCardsM(MATHS);
				MATHS.forEach((card) => {
					if (card?.topicId && card?.subtopicId) {
						addSubtopicToTopicMap(card.topicId, card.subtopicId, 'M');
					}
				});
				const { topics, subtopicsByTopic } = removeUsedSubtopicsFromAvailableList(
					MATHS,
					formattedMathTopic,
					formattedMathSubTopics
				);
				setMathTopics(topics);
				setMathSubtopicsByTopic(subtopicsByTopic);
			}
		};

		const callFunctionsAsynchronously = async () => {
			const {
				formattedMathTopic,
				formattedMathSubTopics,
				formattedReadingTopics,
				formattedReadingSubtopics,
			} = await fetchAndSetTopics();
			fetchAndSetData(
				formattedMathTopic,
				formattedMathSubTopics,
				formattedReadingTopics,
				formattedReadingSubtopics
			);
		};
		callFunctionsAsynchronously();
	}, [userId]);

	const removeSubtopicFromAvailable = (topicId, subtopicId, section) => {
		if (section === 'RW') {
			setReadingSubtopicsByTopic((prev) => {
				const updated = { ...prev };
				if (!updated[topicId]) return prev;

				const filtered = updated[topicId].filter((s) => s.id !== subtopicId);
				if (filtered.length === 0) {
					// Remove topic completely
					const { [topicId]: _, ...rest } = updated;
					setReadingTopics((prevTopics) => prevTopics.filter((t) => t.id !== topicId));
					return rest;
				}
				return { ...updated, [topicId]: filtered };
			});
		} else if (section === 'M') {
			setMathSubtopicsByTopic((prev) => {
				const updated = { ...prev };
				if (!updated[topicId]) return prev;

				const filtered = updated[topicId].filter((s) => s.id !== subtopicId);
				if (filtered.length === 0) {
					const { [topicId]: _, ...rest } = updated;
					setMathTopics((prevTopics) => prevTopics.filter((t) => t.id !== topicId));
					return rest;
				}
				return { ...updated, [topicId]: filtered };
			});
		}
	};

	const addSubtopicBackToAvailable = (topicId, subtopicObj, section) => {
		if (section === 'RW') {
			setReadingSubtopicsByTopic((prev) => {
				const updated = { ...prev };
				const exists = updated[topicId] || [];
				if (exists.find((s) => s.id === subtopicObj.id)) return prev;

				const newSubtopics = [...exists, subtopicObj];
				setReadingTopics((prev) => {
					if (!prev.find((t) => t.id === topicId)) {
						return [
							...prev,
							{ id: topicId, name: topiclist[topicId]?.topicName || '' },
						];
					}
					return prev;
				});
				return { ...updated, [topicId]: newSubtopics };
			});
		} else if (section === 'M') {
			setMathSubtopicsByTopic((prev) => {
				const updated = { ...prev };
				const exists = updated[topicId] || [];
				if (exists.find((s) => s.id === subtopicObj.id)) return prev;

				const newSubtopics = [...exists, subtopicObj];
				setMathTopics((prev) => {
					if (!prev.find((t) => t.id === topicId)) {
						return [
							...prev,
							{ id: topicId, name: topiclist[topicId]?.topicName || '' },
						];
					}
					return prev;
				});
				return { ...updated, [topicId]: newSubtopics };
			});
		}
	};

	const addCard = (section) => {
		if (section === 'RW') {
			setCardsRW((prev) => [...prev, { id: uuid(), topicId: '', subtopicId: '' }]);
		} else if (section === 'M') {
			setCardsM((prev) => [...prev, { id: uuid(), topicId: '', subtopicId: '' }]);
		}
	};

	const removeCard = async (cardId, section) => {
		try {
			let newCardsRW = cardsRW;
			let newCardsM = cardsM;
			if (section === 'RW') {
				if (cardsRW?.length !== 1) {
					newCardsRW = cardsRW.filter((c) => c.id !== cardId);
				} else {
					newCardsRW = [{ id: uuid(), topicId: '', subtopicId: '', saved: false }];
				}
			} else if (section === 'M') {
				if (cardsM?.length !== 1) {
					newCardsM = cardsM.filter((c) => c.id !== cardId);
				} else {
					newCardsM = [{ id: uuid(), topicId: '', subtopicId: '', saved: false }];
				}
			}
			await saveUserKcPathData(userId, newCardsRW, newCardsM);
			if (section === 'RW') {
				const card = cardsRW.find((c) => c.id === cardId);
				if (card?.topicId && card?.subtopicId) {
					removeSubtopicFromTopicMap(card.topicId, card.subtopicId, 'RW');
					addSubtopicBackToAvailable(
						card.topicId,
						{
							id: card.subtopicId,
							name: card.subtopicName,
							progress: card.progress,
						},
						section
					);
				}
				setCardsRW(newCardsRW);
			} else if (section === 'M') {
				const card = cardsM.find((c) => c.id === cardId);
				if (card?.topicId && card?.subtopicId) {
					removeSubtopicFromTopicMap(card.topicId, card.subtopicId, 'M');
					addSubtopicBackToAvailable(
						card.topicId,
						{
							id: card.subtopicId,
							name: card.subtopicName,
							progress: card.progress,
						},
						section
					);
				}
				setCardsM(newCardsM);
			}
		} catch (error) {
			logger.error('Error saving card: ', error);
			setAlert(true);
			setAlertMsg('There was an error removing the card. Please try again later.');
			if (alertTimeoutRef.current) {
				clearTimeout(alertTimeoutRef.current);
			}

			alertTimeoutRef.current = setTimeout(() => {
				setAlert(false);
				alertTimeoutRef.current = null;
			}, 3000);
		}
	};

	const saveCard = async (cardId, section) => {
		try {
			logger.log('cardsRW', cardsRW);
			const newCardsRW = cardsRW.map((card) => {
				if (card.id !== cardId) return card;

				const subtopicData = readingSubtopicsByTopic[card.topicId]?.find(
					(s) => s.id === card.subtopicId
				);
				return {
					...card,
					saved: true,
					subtopicName: subtopicData?.name || '',
					progress: subtopicData?.progress || 0,
				};
			});
			const newCardsM = cardsM.map((card) => {
				if (card.id !== cardId) return card;

				const subtopicData = mathSubtopicsByTopic[card.topicId]?.find(
					(s) => s.id === card.subtopicId
				);

				return {
					...card,
					saved: true,
					subtopicName: subtopicData?.name || '',
					progress: subtopicData?.progress || 0,
				};
			});
			await saveUserKcPathData(userId, newCardsRW, newCardsM);
			logger.log('Card saved successfully');
			if (section === 'RW') {
				// setCardsRW((prev) =>
				// 	prev.map((card) => {
				// 		if (card.id !== cardId) return card;

				// 		const subtopicData = readingSubtopicsByTopic[card.topicId]?.find(
				// 			(s) => s.id === card.subtopicId
				// 		);

				// 		return {
				// 			...card,
				// 			saved: true,
				// 			subtopicName: subtopicData?.name || '',
				// 			progress: subtopicData?.progress || 0,
				// 		};
				// 	})
				// );
				setCardsRW(newCardsRW);
				const card = cardsRW.find((c) => c.id === cardId);
				if (card?.topicId && card?.subtopicId) {
					addSubtopicToTopicMap(card.topicId, card.subtopicId, 'RW');
					const subtopics =
						section === 'RW' ? readingSubtopicsByTopic : mathSubtopicsByTopic;
					const subtopic = subtopics[card.topicId]?.find((s) => s.id === card.subtopicId);
					if (subtopic) {
						removeSubtopicFromAvailable(card.topicId, card.subtopicId, section);
					}
				}
			} else if (section === 'M') {
				// setCardsM((prev) =>
				// 	prev.map((card) => {
				// 		if (card.id !== cardId) return card;

				// 		const subtopicData = mathSubtopicsByTopic[card.topicId]?.find(
				// 			(s) => s.id === card.subtopicId
				// 		);

				// 		return {
				// 			...card,
				// 			saved: true,
				// 			subtopicName: subtopicData?.name || '',
				// 			progress: subtopicData?.progress || 0,
				// 		};
				// 	})
				// );
				setCardsM(newCardsM);
				const card = cardsM.find((c) => c.id === cardId);
				if (card?.topicId && card?.subtopicId) {
					addSubtopicToTopicMap(card.topicId, card.subtopicId, 'M');
					const subtopics =
						section === 'RW' ? readingSubtopicsByTopic : mathSubtopicsByTopic;
					const subtopic = subtopics[card.topicId]?.find((s) => s.id === card.subtopicId);
					if (subtopic) {
						removeSubtopicFromAvailable(card.topicId, card.subtopicId, section);
					}
				}
			}
		} catch (error) {
			logger.error('Error saving card: ', error);
			setAlert(true);
			setAlertMsg('There was an error saving the card. Please try again later.');
			if (alertTimeoutRef.current) {
				clearTimeout(alertTimeoutRef.current);
			}

			alertTimeoutRef.current = setTimeout(() => {
				setAlert(false);
				alertTimeoutRef.current = null;
			}, 3000);
		}
	};

	const handleTopicChange = (cardId, topicId, section) => {
		if (section === 'RW') {
			setCardsRW((prev) =>
				prev.map((c) => (c.id === cardId ? { ...c, topicId, subtopicId: '' } : c))
			);
		} else if (section === 'M') {
			setCardsM((prev) =>
				prev.map((c) => (c.id === cardId ? { ...c, topicId, subtopicId: '' } : c))
			);
		}
	};

	const handleSubtopicChange = (cardId, subtopicId, section) => {
		if (section === 'RW') {
			setCardsRW((prev) => prev.map((c) => (c.id === cardId ? { ...c, subtopicId } : c)));
		} else if (section === 'M') {
			setCardsM((prev) => prev.map((c) => (c.id === cardId ? { ...c, subtopicId } : c)));
		}
	};

	const removeSubtopicFromTopicMap = (topicId, subtopicId, section) => {
		if (section === 'RW') {
			setTopicMapRW((prev) => {
				const topicEntry = prev[topicId];
				if (!topicEntry) return prev;

				const updatedSubtopics = topicEntry.subtopics
					.map((s) => (s.id === subtopicId ? { ...s, count: s.count - 1 } : s))
					.filter((s) => s.count > 0);

				if (updatedSubtopics?.length === 0) {
					const { [topicId]: _, ...rest } = prev;
					return rest;
				}

				return {
					...prev,
					[topicId]: {
						topicId,
						subtopics: updatedSubtopics,
					},
				};
			});
		} else if (section === 'M') {
			setTopicMapM((prev) => {
				const topicEntry = prev[topicId];
				if (!topicEntry) return prev;

				const updatedSubtopics = topicEntry.subtopics
					.map((s) => (s.id === subtopicId ? { ...s, count: s.count - 1 } : s))
					.filter((s) => s.count > 0);

				if (updatedSubtopics?.length === 0) {
					const { [topicId]: _, ...rest } = prev;
					return rest;
				}

				return {
					...prev,
					[topicId]: {
						topicId,
						subtopics: updatedSubtopics,
					},
				};
			});
		}
	};

	const addSubtopicToTopicMap = (topicId, subtopicId, section) => {
		if (section === 'RW') {
			setTopicMapRW((prev) => {
				const topicEntry = prev[topicId] || { topicId, subtopics: [] };
				const subtopics = [...topicEntry.subtopics];

				const existingIndex = subtopics.findIndex((s) => s.id === subtopicId);
				if (existingIndex >= 0) {
					subtopics[existingIndex].count += 1;
				} else {
					subtopics.push({ id: subtopicId, count: 1 });
				}

				return {
					...prev,
					[topicId]: {
						topicId,
						subtopics,
					},
				};
			});
		} else if (section === 'M') {
			setTopicMapM((prev) => {
				const topicEntry = prev[topicId] || { topicId, subtopics: [] };
				const subtopics = [...topicEntry.subtopics];

				const existingIndex = subtopics.findIndex((s) => s.id === subtopicId);
				if (existingIndex >= 0) {
					subtopics[existingIndex].count += 1;
				} else {
					subtopics.push({ id: subtopicId, count: 1 });
				}

				return {
					...prev,
					[topicId]: {
						topicId,
						subtopics,
					},
				};
			});
		}
	};

	const createSFT = async (payload, section) => {
		// const sections = { READING_WRITING: 1, MATHS: 2 };
		// const section = sections?.[selectedMainTab];
		logger.log('Object.values(topicMapRW);', Object.values(topicMapRW));
		try {
			if (payload?.topicDistribution?.length === 0) {
				setAlertMsg('Please select at least one topic');
				setAlert(true);
				if (alertTimeoutRef.current) {
					clearTimeout(alertTimeoutRef.current);
				}
				alertTimeoutRef.current = setTimeout(() => {
					setAlert(false);
					alertTimeoutRef.current = null;
				}, 3000);
				return;
			}
			const response = await api.post(`/sft/createSFTTest`, {
				userId: userId,
				// testType: selectedMainTab,
				// topicDistribution: selectedSubTopics,
				...payload,
			});
			if (!response.data.success) {
				console.warn('API Warning:', response.data.message); // Log warning without crashing
				return null; // Prevent UI error popups
			}
			logger.log('Test created', response.data?.test?.questions);
			dispatch(selectTest('SFT'));
			dispatch(setExamId(response.data?.testId));
			dispatch(updateQuestionData(response.data?.test));
			dispatch(setSection(section));
			dispatch(createTest(payload));
			navigate('/test-instructions');
		} catch (error) {
			console.error('Error creating test: ', error?.response?.data?.message);
			// throw error;
			// alert('There was an error in creating the Test:', error?.response?.data?.message);
			setAlertMsg(error?.response?.data?.message);
			setAlert(true);
			if (alertTimeoutRef.current) {
				clearTimeout(alertTimeoutRef.current);
			}
			alertTimeoutRef.current = setTimeout(() => {
				setAlert(false);
				alertTimeoutRef.current = null;
			}, 3000);
			dispatch(resetExam());
			return;
		}
	};

	// const groupedDataRW = Object.values(topicMapRW);
	// const groupedDataM = Object.values(topicMapM);

	return (
		<>
			{alert && (
				<Alert severity="error" variant="filled">
					{alertMsg}
				</Alert>
			)}
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: '100vw', overflowX: 'hidden' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						textAlign: 'left',
						mb: 4,
						// marginLeft: '8%',
					}}
				>
					<Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
						User Recommended KC Queue
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						gutterBottom
						sx={{ fontWeight: 'bold' }}
					>
						Practice these knowledge components (KCs) Identified by you:
					</Typography>
				</Box>
				<Box
					// display="grid"
					// gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}
					// gap={8}
					sx={{
						// marginLeft: '8%',
						marginTop: '2%',
						width: '100%',
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						gap: 8,
						maxWidth: '100%',
					}}
				>
					<Box>
						<Stack direction="row" spacing={8} sx={{ flex: 1, pt: 2 }}>
							<Stack spacing={2}>
								<Typography
									variant="h6"
									gutterBottom
									sx={{ fontWeight: 'bold', textAlign: 'left' }}
								>
									Reading and Writing States
								</Typography>
								{cardsRW.map((card, index) => {
									return (
										<Box key={index}>
											{!card.saved ? (
												<PathChooseCard
													card={card}
													topics={readingTopics}
													subtopicsByTopic={readingSubtopicsByTopic}
													handleTopicChange={handleTopicChange}
													handleSubtopicChange={handleSubtopicChange}
													onSave={saveCard}
													section="RW"
													key={card.id}
												/>
											) : (
												<PathCard
													index={index}
													kc={{
														title: card.subtopicName,
														subtopics:
															readingSubtopicsByTopic?.[card.topicId],
														subtopicId: card.subtopicId,
													}}
													id={card.id}
													onAdd={addCard}
													onRemove={removeCard}
													section="RW"
												/>
											)}
										</Box>
									);
								})}
							</Stack>
						</Stack>
					</Box>
					<Box>
						<Stack direction="row" spacing={8} sx={{ flex: 1, pt: 2 }}>
							<Stack spacing={2}>
								<Typography
									variant="h6"
									gutterBottom
									sx={{ fontWeight: 'bold', textAlign: 'left' }}
								>
									Maths
								</Typography>
								{cardsM.map((card, index) => {
									return (
										<Box key={index}>
											{!card.saved ? (
												<PathChooseCard
													card={card}
													topics={mathTopics}
													subtopicsByTopic={mathSubtopicsByTopic}
													handleTopicChange={handleTopicChange}
													handleSubtopicChange={handleSubtopicChange}
													onSave={saveCard}
													section="M"
													key={card.id}
												/>
											) : (
												<PathCard
													index={index}
													kc={{
														title: card.subtopicName,
														mastery: 'Low',
														progress: Math.floor(Math.random() * 100),
													}}
													id={card.id}
													onAdd={addCard}
													onRemove={removeCard}
													section="M"
												/>
											)}
										</Box>
									);
								})}
							</Stack>
						</Stack>
					</Box>
					<Box sx={{ width: { xs: '100%', sm: '30%' }, height: 'fit-content' }}>
						<Card sx={{ height: '100%' }}>
							<CardContent>
								<Typography
									variant="h6"
									gutterBottom
									sx={{ fontWeight: 'bold', textAlign: 'left' }}
								>
									SFT Test
								</Typography>
								{/* <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Based on your performance, here’s the <strong>suggested</strong>{' '}
                                                        type of SAT practice test for you today.
                                                    </Typography> */}
								<Box
									sx={{
										height: '100%',
										alignItems: 'center',
										justifyContent: 'center',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											mt: 3,
											p: 2,
											border: '1px solid #e0e0e0',
											borderRadius: '12px',
											backgroundColor: '#fff',
											width: '100%',
											maxWidth: '100%',
										}}
									>
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'row',
												alignItems: 'center',
											}}
										>
											<Box sx={{ mr: 2 }}>
												<img src={SftAiPathIcon} alt="Test Icon" />
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													textAlign: 'left',
												}}
											>
												<Typography variant="subtitle1" fontWeight="bold">
													Reading & Writing SFT Test
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Comprehensive Practice
												</Typography>
											</Box>
										</Box>
										<Box sx={{ marginLeft: '1rem' }}>
											<Button
												variant="contained"
												color="primary"
												onClick={() =>
													createSFT(
														{
															testType: 'READING_WRITING',
															topicDistribution: [
																...Object.values(topicMapRW),
															],
														},
														1
													)
												}
											>
												Start
											</Button>
										</Box>
									</Box>
									<Box
										mt={3}
										p={2}
										display="flex"
										justifyContent="space-between"
										alignItems="center"
										sx={{
											border: '1px solid #e0e0e0',
											borderRadius: '12px',
											backgroundColor: '#fff',
											width: '100%',
										}}
									>
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'row',
												alignItems: 'center',
											}}
										>
											<Box sx={{ mr: 2 }}>
												<img src={SftAiPathIcon} alt="Test Icon" />
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													textAlign: 'left',
												}}
											>
												<Typography variant="subtitle1" fontWeight="bold">
													Maths SFT Test
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Comprehensive Practice
												</Typography>
											</Box>
										</Box>
										<Button
											variant="contained"
											color="primary"
											onClick={() =>
												createSFT(
													{
														testType: 'MATHS',
														topicDistribution: [
															...Object.values(topicMapM),
														],
													},
													1
												)
											}
										>
											Start
										</Button>
									</Box>
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default MyPathDashboard;
